<script lang="ts" setup>
import { ProjectStatus } from '@/helper/Types.ts'
import { ref } from 'vue'
import userStore from '@/store/UserStore.ts'
import PreviewProjectModel from '@/models/PreviewProjectModel.ts'
import { api } from '@/services/ApiService.ts'
import { copyToClipboard, stripPersonalInformation } from '@/helper/tools.ts'

const props = defineProps<{
  project: PreviewProjectModel
}>()

const emit = defineEmits<{
  (e: 'download', target: EventTarget | null, projectId: string): void
  (e: 'delete', projectId: string): void
  (e: 'copy', projectId: string, projectName: string): void
  (e: 'edit', projectId: string, status: ProjectStatus): void
}>()

const el = ref<HTMLElement | null>(null)
const isOpen = ref(false)

function blurred(e: FocusEvent): void {
  if (!el.value || el.value?.contains(e.relatedTarget as HTMLElement)) {
    return
  }
  if (e.target === e.relatedTarget) {
    return
  }
  isOpen.value = false
}

function toggleClicked(e: Event): void {
  e.preventDefault()
  isOpen.value = !isOpen.value
  const button: HTMLElement | null = e.target as HTMLElement
  button.focus()
}

function downloadClicked(e: Event): void {
  isOpen.value = false
  emit('download', e.currentTarget, props.project.id)
}

function deleteClicked(): void {
  isOpen.value = false
  emit('delete', props.project.id)
}

function copyClicked(): void {
  isOpen.value = false
  emit('copy', props.project.id, props.project.name)
}

function editClicked(): void {
  isOpen.value = false
  emit('edit', props.project.id, props.project.status)
}

async function downloadSimulationDebugArchive() {
  isOpen.value = false
  if (props.project.debugArchiveUrl) {
    await api.downloadUrl(props.project.debugArchiveUrl)
  }
}

async function copyProjectJson() {
  isOpen.value = false

  const project = await api.loadProject(props.project.id)
  stripPersonalInformation(project)

  const projectJson = JSON.stringify(project, null, 2)
  copyToClipboard(projectJson)
}
</script>

<template>
  <div ref="el" :class="{ 'is--open': isOpen }" class="action-menu">
    <button class="btn-plain action-menu__btn action-menu__toggle" @focusout="blurred" @click.stop="toggleClicked">
      <i class="ic-more" />
    </button>

    <div class="action-menu__dropdown">
      <template v-if="userStore.isPrivileged">
        <button
          v-if="project.debugArchiveUrl" class="btn-plain action-menu__btn is--download"
          @click.stop="downloadSimulationDebugArchive"
        >
          <span>Simulation Debug Archive</span>
          <i class="ic is--download" />
        </button>

        <button class="btn-plain action-menu__btn" @click.stop="copyProjectJson">
          <span>Copy JSON</span>
          <i class="ic-copy" />
        </button>
      </template>

      <button class="btn-plain action-menu__btn is--delete" @click.stop="deleteClicked">
        <span>{{ $t('overview.table_btn_delete') }}</span>
        <i class="ic-trash" />
      </button>

      <button class="btn-plain action-menu__btn is--copy" @click.stop="copyClicked">
        <span>{{ $t('overview.table_btn_copy') }}</span>
        <i class="ic-copy" />
      </button>

      <button
        v-if="project.status === ProjectStatus.COMPLETE"
        class="btn-plain action-menu__btn is--download"
        @click.stop="downloadClicked"
      >
        <span>{{ $t('overview.table_btn_download') }}</span>
        <i class="ic is--download" />
      </button>

      <button class="btn-plain action-menu__btn is--edit" @click.stop="editClicked">
        <span>{{ $t('overview.table_btn_edit_project') }}</span>
        <i class="ic-edit" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.action-menu {
  position: relative;
  margin-left: 0;

  &__btn {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    color: $textColor;
    cursor: pointer;
    font-size: rem-calc(16);
    height: rem-calc(42);
    margin: 0 rem-calc(8);

    [class^='ic-'],
    [class*=' ic-'],
    .ic {
      font-size: rem-calc(26);
      width: rem-calc(24);
      height: rem-calc(24);
      line-height: rem-calc(24);
      display: flex;
      justify-content: center;
      align-items: center;
      margin-left: rem-calc(8);
      transition: transform $animTimeShort ease;

      &:before {
        line-height: rem-calc(24);
      }

      @include themify($themes) {
        color: themed('primaryColor');
      }
    }

    &.is--delete {
      [class^='ic-'],
      [class*=' ic-'],
      .ic {
        &:hover {
          &:before {
            @extend .ic-trash-open;
          }
        }
      }
    }

    &.is--copy {
      [class^='ic-'],
      [class*=' ic-'],
      .ic {
        &:hover {
          &:before {
            @extend .ic-copy-hover;
          }
        }
      }
    }

    .ic.is--download {
      @extend .ic-arrow-to-bottom;

      &:hover {
        @extend .ic-arrow-to-bottom-hover;
      }
    }

    &.is--downloading {
      outline: none;

      .ic.is--download {
        animation: spin 1.3s infinite cubic-bezier(0.5, 0.1, 0.5, 0.9);
        font-size: rem-calc(20);
        @extend .ic-sync2;

        &:hover {
          @extend .ic-sync2;
        }

        &:before {
          line-height: rem-calc(30);
        }
      }
    }
  }

  &__toggle {
    margin-left: 0;
    padding: 0;
    background: $tableBorderColor;
    margin-right: 0;
    width: rem-calc(30);
    height: rem-calc(30);
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    [class^='ic-'],
    [class*=' ic-'] {
      font-size: rem-calc(25);
      margin-left: 0;
    }

    @include themify($themes) {
      border-radius: rem-calc(themed('inputBorderRadius'));
    }

    @include themify($themes) {
      color: themed('primaryColor');
    }
  }

  &__dropdown {
    display: flex;
    flex-direction: column-reverse;
    position: absolute;
    top: 85%;
    right: rem-calc(24);
    z-index: 2;
    background: #fff;
    min-width: rem-calc(172);
    visibility: hidden;
    opacity: 0;
    transform: scale3d(1, 0, 1);
    transform-origin: top;
    transition: opacity $animTimeShort ease, visibility 0s ease $animTimeShort, transform 0s $animTimeShort ease;

    @include componentShadow();
    @include themify($themes) {
      border-radius: rem-calc(themed('inputBorderRadius'));
    }

    .is--open & {
      visibility: visible;
      opacity: 1;
      transform: scale3d(1, 1, 1);
      transition: opacity $animTimeShort ease, visibility 0s ease 0s, transform 0.15s ease;
    }
  }
}
</style>
